/* ThreadKalaa product card behaviours
 * ------------------------------------
 *   1. Wishlist heart  — localStorage backed, syncs across cards & tabs
 *   2. Image carousel  — hover swap on the product image stack
 *   3. Quick-add bag   — popover for size selection, AJAX add via Dawn cart
 *
 * Loaded once per page; uses event delegation so it survives facet re-renders.
 */
(function () {
  'use strict';

  if (window.__tkProductCardInit) return;
  window.__tkProductCardInit = true;

  /* ============================================================
     Wishlist code removed — superseded by assets/tk-wishlist.js
     (loaded globally from layout/theme.liquid). Keeping both files
     wired the same listener twice and silently cancelled toggles:
     the first listener added the item, the second saw it existed
     and removed it. All wishlist behaviour now lives in
     tk-wishlist.js, which exposes window.TKWishlist.
     ============================================================ */

  /* ============================================================
     Quick-add (AJAX cart) — fast path, no Dawn drawer popup
     ============================================================ */
  function ajaxAddToCart(variantId, button) {
    if (!variantId || !button) return;
    if (button.classList.contains('is-loading')) return;

    button.classList.add('is-loading');
    button.classList.remove('is-success');

    /* Minimal payload — request ONLY the small cart-icon-bubble section
       in the add response (~30ms extra). The big cart-drawer section is
       refreshed asynchronously after the toast shows (see refreshCartDrawerSilently). */
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('sections', 'cart-icon-bubble');
    formData.append('sections_url', window.location.pathname);

    var routes = (window.Shopify && window.Shopify.routes) || {};
    var addUrl = (routes.root || '/') + 'cart/add.js';
    addUrl = addUrl.replace('//', '/');

    fetch(addUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        button.classList.remove('is-loading');

        if (!result.ok || result.data.status) {
          flashError(button);
          var msg =
            (result.data && result.data.description) ||
            (result.data && result.data.message) ||
            'Could not add to bag.';
          showErrorToast(msg);
          return;
        }

        button.classList.add('is-success');
        setTimeout(function () {
          button.classList.remove('is-success');
        }, 1800);

        /* Subtle "Added" toast — no drawer popup */
        showAddedToast();

        /* Update header cart count bubble from the section HTML in the response.
           Shopify already rendered it server-side, so this is instant — no extra fetch. */
        if (result.data && result.data.sections && result.data.sections['cart-icon-bubble']) {
          updateBubbleFromHTML(result.data.sections['cart-icon-bubble']);
        }

        /* Refresh the cart-drawer's inner HTML in the BACKGROUND so when the user
           clicks the cart icon it shows the correct items. Doesn't block the toast. */
        refreshCartDrawerSilently();

        /* Fire Dawn pub/sub event so any header / cart-icon-bubble listeners
           refresh themselves WITHOUT opening the cart drawer. */
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          try {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'tk-quickadd',
              productVariantId: variantId,
              cartData: result.data,
            });
          } catch (e) {
            /* ignore */
          }
        }

        document.dispatchEvent(
          new CustomEvent('tk:cart:added', {
            detail: { variantId: variantId, cart: result.data },
          })
        );
      })
      .catch(function (err) {
        button.classList.remove('is-loading');
        flashError(button);
        // eslint-disable-next-line no-console
        console.error('TK quick add failed:', err);
      });
  }

  function flashError(button) {
    button.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-3px)' },
        { transform: 'translateX(3px)' },
        { transform: 'translateX(0)' },
      ],
      { duration: 280, iterations: 1 }
    );
  }

  /* Replace the inner HTML of #cart-icon-bubble with the section HTML Shopify
     returned in the add response. Section HTML wraps everything in a
     <div id="shopify-section-cart-icon-bubble"> — we extract just the inner. */
  function updateBubbleFromHTML(sectionHTML) {
    var host = document.getElementById('cart-icon-bubble');
    if (!host || !sectionHTML) return;
    try {
      var parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
      /* Try section wrapper first, then fall back to first element */
      var source =
        parsed.querySelector('#shopify-section-cart-icon-bubble') ||
        parsed.body.firstElementChild;
      if (source) host.innerHTML = source.innerHTML;
    } catch (e) {
      /* ignore */
    }
  }

  /* Silently refresh Dawn's cart-drawer HTML in the background — runs in
     parallel with our toast so the user sees instant feedback. By the time
     they click the cart icon (~1-2s later), the drawer is up to date. */
  function refreshCartDrawerSilently() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return; /* No drawer on this theme variant — bubble alone suffices */

    var routes = (window.Shopify && window.Shopify.routes) || {};
    var url = (routes.root || '/') + '?sections=cart-drawer';
    url = url.replace('//', '/');

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        if (!sections || !sections['cart-drawer']) return;
        var parsed = new DOMParser().parseFromString(
          sections['cart-drawer'],
          'text/html'
        );
        /* Swap the .drawer__inner contents — preserves the cart-drawer custom
           element, its event listeners, and its open/close state. */
        var newInner = parsed.querySelector('.drawer__inner');
        var existingInner = drawer.querySelector('.drawer__inner');
        if (newInner && existingInner) {
          existingInner.innerHTML = newInner.innerHTML;
          existingInner.classList.remove('is-empty');
        }
      })
      .catch(function () {
        /* silent fail — user can hard-refresh if needed */
      });
  }

  /* Subtle "Added to bag" toast — replaces Dawn's cart drawer popup. */
  function showAddedToast() {
    var existing = document.querySelector('.tk-cart-toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.className = 'tk-cart-toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>' +
      '<span>Added to bag</span>' +
      '<button type="button" class="tk-cart-toast__link" data-tk-open-drawer>View bag</button>';
    document.body.appendChild(toast);

    /* Force reflow then apply visible state for transition */
    // eslint-disable-next-line no-unused-expressions
    toast.offsetHeight;
    toast.classList.add('is-visible');

    clearTimeout(window.__tkToastTimer);
    window.__tkToastTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 280);
    }, 2400);
  }

  function showErrorToast(message) {
    var existing = document.querySelector('.tk-cart-toast--error');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.className = 'tk-cart-toast tk-cart-toast--error';
    toast.setAttribute('role', 'alert');
    toast.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>' +
      '<span>' + message + '</span>';
    document.body.appendChild(toast);

    toast.offsetHeight;
    toast.classList.add('is-visible');

    clearTimeout(window.__tkErrorToastTimer);
    window.__tkErrorToastTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 280);
    }, 3500);
  }

  function closeAllPopovers(except) {
    document
      .querySelectorAll('[data-tk-quickadd].is-open')
      .forEach(function (el) {
        if (el !== except) el.classList.remove('is-open');
      });
  }

  /* ============================================================
     Click delegation (capture phase so we beat link::after)
     ============================================================ */
  document.addEventListener(
    'click',
    function (e) {
      /* Wishlist clicks handled by tk-wishlist.js — see comment at top. */

      /* View-bag button in the "Added to bag" toast → open cart drawer
         (do NOT navigate to /cart). The drawer is the canonical cart
         surface; /cart page stays as a fallback for anyone who hits
         the URL directly but no surface in the UI links to it. */
      var openDrawer = e.target.closest('[data-tk-open-drawer]');
      if (openDrawer) {
        e.preventDefault();
        e.stopPropagation();
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        } else {
          /* Fallback — click the header cart icon, which Dawn wires
             to open the drawer when settings.cart_type === 'drawer'. */
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble) bubble.click();
        }
        return;
      }

      /* 2026-10-28: Main "Add to Cart" button under price (multi-variant cards)
         proxies to the sibling tk-quickadd bag icon trigger. This gives the
         same inline-pill popover UX as the bag icon, instead of opening Dawn's
         full product preview modal. Single-variant cards use their own
         product-form submit (not this path).
         Fix 2026-10-29: use .card-wrapper or .card (NOT [class*="card"] —
         that would match .card__information first, which doesn't contain
         the tk-quickadd sibling). */
      var mainTrig = e.target.closest('[data-tk-main-trigger]');
      if (mainTrig) {
        e.preventDefault();
        e.stopPropagation();
        var cardWrap = mainTrig.closest('.card-wrapper') || mainTrig.closest('.card') || mainTrig.closest('.grid__item');
        var proxyTrigger = cardWrap && cardWrap.querySelector('[data-tk-quickadd-trigger]');
        if (proxyTrigger) {
          proxyTrigger.click();
        }
        return;
      }

      /* Bag trigger */
      var trigger = e.target.closest('[data-tk-quickadd-trigger]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        var quickadd = trigger.closest('[data-tk-quickadd]');
        var hasPopover =
          quickadd && quickadd.querySelector('[data-tk-quickadd-popover]');
        var variantsCount = parseInt(
          trigger.getAttribute('data-variants-count'),
          10
        );
        var hasSize = trigger.getAttribute('data-has-size') === '1';
        var variantId = trigger.getAttribute('data-variant-id');

        if (hasPopover && hasSize && variantsCount > 1) {
          var willOpen = !quickadd.classList.contains('is-open');
          closeAllPopovers(quickadd);
          quickadd.classList.toggle('is-open', willOpen);
        } else {
          ajaxAddToCart(variantId, trigger);
        }
        return;
      }

      /* Size pill in popover */
      var sizeBtn = e.target.closest('[data-tk-quickadd-size]');
      if (sizeBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (sizeBtn.getAttribute('data-variant-available') === '0') return;
        var quickadd2 = sizeBtn.closest('[data-tk-quickadd]');
        var triggerBtn = quickadd2
          ? quickadd2.querySelector('[data-tk-quickadd-trigger]')
          : null;
        var vid = sizeBtn.getAttribute('data-variant-id');
        if (vid && triggerBtn) {
          ajaxAddToCart(vid, triggerBtn);
          if (quickadd2) quickadd2.classList.remove('is-open');
        }
        return;
      }

      /* Click outside any quickadd → close all */
      if (!e.target.closest('[data-tk-quickadd]')) {
        closeAllPopovers();
      }
    },
    true /* capture phase */
  );

  /* ============================================================
     Image carousel — hover swap (per card)
     ============================================================ */
  function initCarousel(card) {
    if (card.__tkCarouselInit) return;
    card.__tkCarouselInit = true;

    var stack = card.querySelector('[data-tk-card-stack]');
    var dotsRoot = card.querySelector('[data-tk-card-dots]');
    if (!stack || !dotsRoot) return;

    var stackImgs = stack.querySelectorAll('.tk-card-media-stack__img');
    var dots = dotsRoot.querySelectorAll('.tk-card-dots__dot');

    var currentIndex = 0;

    function show(index) {
      if (index === currentIndex) return;
      currentIndex = index;

      stackImgs.forEach(function (img) {
        var idx = parseInt(img.getAttribute('data-index'), 10);
        if (idx === index) {
          img.classList.add('is-visible');
        } else {
          img.classList.remove('is-visible');
        }
      });

      dots.forEach(function (d, i) {
        if (i === index) d.classList.add('is-active');
        else d.classList.remove('is-active');
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('mouseenter', function () {
        var idx = parseInt(dot.getAttribute('data-index'), 10);
        if (!isNaN(idx)) show(idx);
      });
    });

    card.addEventListener('mouseleave', function () {
      show(0);
    });
  }

  function bootstrapCarousels() {
    document.querySelectorAll('.card-wrapper').forEach(initCarousel);
  }

  /* ============================================================
     Initial pass + debounced observer for facet/pagination swaps
     ============================================================ */
  var refreshTimer = null;
  function syncWishlistButtons() {
    if (typeof window.syncWishlistButtons === 'function') {
      window.syncWishlistButtons();
    }
  }
  function scheduleRefresh() {
    if (refreshTimer) return;
    refreshTimer = window.requestAnimationFrame(function () {
      refreshTimer = null;
      syncWishlistButtons();
      bootstrapCarousels();
    });
  }

  function initialPass() {
    syncWishlistButtons();
    bootstrapCarousels();
  }

  function startObserver() {
    if (!document.body) return;
    var observer = new MutationObserver(scheduleRefresh);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initialPass();
      startObserver();
    });
  } else {
    initialPass();
    startObserver();
  }
})();

/* ============================================================
   Festive Picks — mobile 2x2 pagination dots with active-state tracking
   2026-10-29 per user ask: static dots weren't enough; need visual feedback
   showing which page the user is currently viewing.

   Behavior:
   - Only runs on mobile viewport (<=749px)
   - Finds the festive section's product-grid
   - Counts pages (products / 4, rounded up)
   - Injects dot elements after the grid
   - Listens to grid scroll; toggles .is-active on dot matching current page
   - Safe: no-op on desktop, no-op if < 2 pages, idempotent via data flag
   ============================================================ */
(function initFestiveDots() {
  if (typeof window === 'undefined' || !window.matchMedia) return;
  var mql = window.matchMedia('(max-width: 749px)');

  function setupSection(section) {
    var grid = section.querySelector('.product-grid');
    if (!grid || grid.dataset.tkFpInit === '1') return;

    var items = grid.querySelectorAll('.grid__item').length;
    var pages = Math.max(1, Math.ceil(items / 4));
    if (pages < 2) return;
    grid.dataset.tkFpInit = '1';

    /* Build dots container */
    var dotsWrap = document.createElement('div');
    dotsWrap.className = 'tk-festive-dots';
    dotsWrap.setAttribute('role', 'tablist');
    dotsWrap.setAttribute('aria-label', 'Festive picks pagination');
    for (var i = 0; i < pages; i++) {
      var d = document.createElement('span');
      d.className = 'tk-fp-dot' + (i === 0 ? ' is-active' : '');
      d.setAttribute('role', 'tab');
      d.setAttribute('aria-label', 'Page ' + (i + 1) + ' of ' + pages);
      dotsWrap.appendChild(d);
    }
    /* Insert AFTER the grid (as sibling), inside the slider-component wrapper */
    if (grid.parentNode) {
      grid.parentNode.insertBefore(dotsWrap, grid.nextSibling);
    }

    /* Scroll tracking — debounced via rAF */
    var ticking = false;
    function updateActiveDot() {
      ticking = false;
      var pageWidth = grid.scrollWidth / pages;
      if (pageWidth <= 0) return;
      var currentPage = Math.round(grid.scrollLeft / pageWidth);
      if (currentPage < 0) currentPage = 0;
      if (currentPage >= pages) currentPage = pages - 1;
      var allDots = dotsWrap.querySelectorAll('.tk-fp-dot');
      for (var j = 0; j < allDots.length; j++) {
        if (j === currentPage) {
          if (!allDots[j].classList.contains('is-active')) {
            allDots[j].classList.add('is-active');
          }
        } else {
          allDots[j].classList.remove('is-active');
        }
      }
    }
    grid.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(updateActiveDot);
        ticking = true;
      }
    }, { passive: true });
  }

  function setupAll() {
    var sections = document.querySelectorAll('.shopify-section[id$="__tk_festive_picks"]');
    for (var i = 0; i < sections.length; i++) {
      setupSection(sections[i]);
    }
  }

  function maybeRun() {
    if (mql.matches) setupAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeRun);
  } else {
    maybeRun();
  }
  /* Re-run if viewport crosses the breakpoint (user rotates phone, etc) */
  if (mql.addEventListener) {
    mql.addEventListener('change', function (e) { if (e.matches) setupAll(); });
  } else if (mql.addListener) {
    mql.addListener(function (e) { if (e.matches) setupAll(); });
  }
})();
